/**
 * Rotating status messages + pro tips for draft loading phases.
 *
 * SOURCE: frontend-prod/features/draft/DraftCreationLoader.tsx:23-160
 *  - Web has ~135 messages; we ship a curated subset tuned for the
 *    Indian legal context the plugin targets.
 *  - We deliberately avoid Framer Motion (web's framework choice) and
 *    rely on CSS opacity transitions in DraftLoadingOverlay.
 *
 * To extend / sync: add lines here, do not change the consumer.
 */

export const TEMPLATE_MESSAGES: readonly string[] = [
  'Analyzing your brief',
  'Identifying document type',
  'Mapping out sections',
  'Drafting the outline',
  'Surveying standard clauses',
  'Cross-checking conventions',
  'Selecting the right structure',
  'Composing section headers',
  'Annotating depth and complexity',
  'Reviewing must-include items',
  'Finalizing the template',
  'Almost there',
];

export const QUESTIONS_MESSAGES: readonly string[] = [
  'Identifying key clarifications',
  'Composing follow-up questions',
  'Looking for fields we need from you',
  'Spotting placeholders worth confirming',
  'Reading the template carefully',
  'Drafting smart questions',
  'Reviewing the question set',
  'Almost ready for your answers',
];

export const DRAFT_MESSAGES: readonly string[] = [
  'Reading your answers',
  'Drafting Section 1',
  'Citing relevant statutes',
  'Consulting the BNS and IPC',
  'Reviewing applicable case law',
  'Composing precise legalese',
  'Filling defined terms',
  'Cross-referencing clauses',
  'Building the recitals',
  'Composing the operative provisions',
  'Drafting representations and warranties',
  'Drafting the indemnity clauses',
  'Adding boilerplate (jurisdiction, severability, notices)',
  'Inserting the signature block',
  'Validating section numbering',
  'Checking internal cross-references',
  'Polishing language and tone',
  'Grab a chai — almost done',
  'Reviewing once more for missed bits',
  'Compiling the final document',
  'Saving to your draft library',
  'Putting the finishing touches',
  'Wrapping things up',
];

/**
 * Used when F1 Auto/Instant mode is selected — emphasises the
 * "no questions asked" path while the agent goes brief→draft directly.
 */
export const INSTANT_DRAFT_MESSAGES: readonly string[] = [
  'Reading your brief',
  'Skipping the questions — going straight to drafting',
  'Filling in plausible defaults',
  'Drafting Section 1',
  'Cross-referencing clauses',
  'Reviewing case law',
  'Composing the operative provisions',
  'Adding boilerplate (jurisdiction, severability)',
  'Inserting the signature block',
  'Polishing the language',
  'Almost done — this is the express lane',
  'Compiling the final document',
];

/**
 * Research-mode thinking messages.
 * SOURCE: frontend-prod/features/research/components/ResearchThinkingLoader.tsx:13-37
 * Curated subset tuned for the Indian legal context.
 */
export const RESEARCH_THINKING_MESSAGES: readonly string[] = [
  'Reading your question',
  'Parsing the legal issue',
  'Mapping the facts',
  'Searching the precedents',
  'Looking through case law',
  'Cross-referencing statutes',
  'Consulting the BNS and IPC',
  'Reviewing Supreme Court rulings',
  'Checking High Court decisions',
  'Cross-checking the CPC',
  'Pulling together citations',
  'Weighing the arguments',
  'Sifting through authorities',
  'Synthesising the findings',
  'Almost there',
];

/**
 * Edit-mode thinking messages — shorter, lower-key.
 */
export const EDIT_THINKING_MESSAGES: readonly string[] = [
  'Reading the document',
  'Locating the passage',
  'Composing the edit',
  'Reviewing the change',
  'Finalising tracked changes',
  'Almost done',
];

export const PRO_TIPS: readonly string[] = [
  'Tip: You can stop anytime — your progress so far is saved.',
  'Tip: Pin the add-in via the dropdown next to the Home tab so it sticks.',
  'Tip: Attach prior research threads to ground your draft in case law.',
  'Tip: Concise mode is faster; Detailed mode is more thorough.',
  'Tip: Tracked changes show up natively in Word — Accept / Reject from the Review ribbon.',
  'Tip: Use Auto mode to skip questions and go straight from brief to draft.',
];
